/* eslint-disable @atlaskit/design-system/use-primitives-text */
/* eslint-disable @atlaskit/design-system/use-heading */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { token } from '@atlaskit/tokens';

import suggestedAppsCoverIcon from '../../../../../assets/suggested-apps-cover.svg';
import { useAnalyticsEvents } from '../../../../../common/analytics/generated/use-analytics-events';
import { SectionName } from '../../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../../common/analytics/useSectionReveal';
import type { OrgMetricsResponse } from '../../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../../SpacingWrapper';
import { STATIC_CONNECTED_APPS_DATA } from '../static-data';

import { messages } from './messages';

const colors = ['#82B536', '#BF63F3', '#FCA700', '#1868DB'];

const styles = cssMap({
	section: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		paddingInline: token('space.400'),
		// NO ADS token exists for 120px
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.600'),
	},
	title: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.heading.xxlarge'),
		fontFamily: token('font.family.brand.heading'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		margin: 0,
	},
	description: {
		color: token('color.text.subtlest'),
		font: token('font.body.large'),
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.75rem',
	},
	appsList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	appRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: token('space.250'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1F1F21',
		borderRadius: token('radius.large'),
		paddingInline: token('space.200'),
		paddingBlock: token('space.200'),
	},
	appIcon: {
		borderRadius: token('radius.large'),
		width: '32px',
		height: '32px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	appName: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.body.large'),
		margin: 0,
		width: '128px',
		flexShrink: 0,
		'@media (max-width: 30rem)': {
			width: '100px',
		},
	},
	progressBarTrack: {
		flex: '2 1 auto',
		height: '6px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#2C2C2E',
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
	itemCount: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#7D818A',
		font: token('font.body'),
		margin: 0,
		minWidth: '80px',
		textAlign: 'right',
	},
	showMoreButton: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#4D8CED',
		background: 'none',
		border: 'none',
		padding: 0,
		font: token('font.body'),
		cursor: 'pointer',
		alignSelf: 'flex-start',
	},
	suggestedSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
	},
	suggestedTitle: {
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
		margin: 0,
	},
	suggestedDescription: {
		color: token('color.text.subtlest'),
		font: token('font.body.large'),
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.75rem',
	},
	suggestedAppsList: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, 1fr)',
		gap: token('space.300'),
		'@media (max-width: 30rem)': {
			gridTemplateColumns: 'repeat(2, 1fr)',
		},
	},
	suggestedApp: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		flexGrow: 1,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1F1F21',
		borderRadius: token('radius.xlarge'),
		paddingInline: token('space.400'),
		paddingBlock: token('space.400'),
		height: '240px',
		'@media (max-width: 30rem)': {
			flexBasis: '50%',
			height: '208px',
		},
	},
	suggestedAppIcon: {
		borderRadius: token('radius.large'),
		width: '32px',
		height: '32px',
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	suggestedAppDetails: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	suggestedAppName: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFF',
		font: token('font.body.large'),
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.75',
	},
	teammatesLabel: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.25',
	},
	connectButton: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFFFFF',
		border: 'none',
		borderRadius: token('radius.medium'),
		paddingInline: token('space.100'),
		paddingBlock: token('space.050'),
		font: token('font.body'),
		cursor: 'pointer',
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderWidth: '1.5px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#505258',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		justifyContent: 'center',
	},
	progressBarFill: {
		height: '100%',
		borderRadius: token('radius.small'),
	},
	allConnectorsIcon: {
		height: '32px',
		width: 'auto',
	},
});

type ProgressBarFillProps = {
	color: string;
	widthPercent: number;
};

function ProgressBarFill({ color, widthPercent }: ProgressBarFillProps): JSX.Element {
	return (
		<div
			css={styles.progressBarFill}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			style={{
				width: `${widthPercent}%`,
				backgroundColor: color,
			}}
		/>
	);
}

type ConnectedAppsSectionProps = {
	metrics: OrgMetricsResponse;
};

export const ConnectedAppsSection = ({ metrics }: ConnectedAppsSectionProps): JSX.Element => {
	const { formatNumber } = useIntl();
	const { initialVisibleCount, suggestedApps } = STATIC_CONNECTED_APPS_DATA;
	const [expanded, setExpanded] = useState(false);
	const { fireEvent } = useAnalyticsEvents();
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.ConnectedApps);

	const handleToggleExpanded = useCallback((): void => {
		setExpanded((prev) => {
			const next = !prev;
			fireEvent('ui.connectedAppsList.toggled.twgMicrositeConnectedApps', {
				expanded: next,
			});
			return next;
		});
	}, [fireEvent]);

	const handleConnectMoreApps = useCallback((): void => {
		fireEvent('ui.connectMoreApps.clicked.twgMicrositeConnectedApps', {});
		window.open('https://www.atlassian.com/software/rovo/connectors', '_blank');
	}, [fireEvent]);

	const connectorObjectsByProvider = metrics.connectorObjectsByProvider ?? [];
	const maxCount = Math.max(...connectorObjectsByProvider.map((a) => a.objects30d));
	const visibleApps = expanded
		? connectorObjectsByProvider
		: connectorObjectsByProvider.slice(0, initialVisibleCount);
	const hiddenCount = connectorObjectsByProvider.length - initialVisibleCount;

	return (
		<SpacingWrapper>
			<section ref={sectionRef} css={styles.section}>
				<div css={styles.content}>
					<div>
						<h2 css={styles.title}>
							<FormattedMessage {...messages.title} />
						</h2>
						<p css={styles.description}>
							<FormattedMessage
								{...messages.description}
								values={{ appCount: metrics.totalAppsInstalled ?? 0 }}
							/>
						</p>
					</div>

					<div css={styles.appsList}>
						{visibleApps.map((app, index) => (
							<div key={app.providerName} css={styles.appRow}>
								<div css={styles.appIcon}></div>
								<p css={styles.appName}>{app.providerName.replace('provider', '').trim()}</p>
								<div css={styles.progressBarTrack}>
									<ProgressBarFill
										color={colors[index % colors.length]}
										widthPercent={Math.round((app.objects30d / maxCount) * 100)}
									/>
								</div>
								<p css={styles.itemCount}>
									<FormattedMessage
										{...messages.itemsCountLabel}
										values={{
											count: formatNumber(app.objects30d),
										}}
									/>
								</p>
							</div>
						))}
					</div>

					{hiddenCount > 0 && (
						<button css={styles.showMoreButton} type="button" onClick={handleToggleExpanded}>
							{expanded ? (
								<FormattedMessage {...messages.showLessButton} />
							) : (
								<FormattedMessage {...messages.showMoreButton} values={{ count: hiddenCount }} />
							)}
						</button>
					)}

					<div css={styles.suggestedSection}>
						<div>
							<p css={styles.suggestedTitle}>
								<FormattedMessage {...messages.suggestedAppsTitle} />
							</p>
							<p css={styles.suggestedDescription}>
								<FormattedMessage {...messages.suggestedAppsDescription} />
							</p>
						</div>
						<div css={styles.suggestedAppsList}>
							{suggestedApps.map((app) => (
								<div key={app.source} css={styles.suggestedApp}>
									<div css={styles.suggestedAppIcon}></div>
									<div css={styles.suggestedAppDetails}>
										<div>
											<p css={styles.suggestedAppName}>{app.name}</p>
											<p css={styles.teammatesLabel}>
												<FormattedMessage
													{...messages.teammatesUsingLabel}
													values={{
														count: formatNumber(app.teammatesUsing),
													}}
												/>
											</p>
										</div>

										<button css={styles.connectButton} type="button">
											<FormattedMessage {...messages.connectButton} />
											<ArrowRightIcon label="arrow right" />
										</button>
									</div>
								</div>
							))}
							<div key="all-apps" css={styles.suggestedApp}>
								{/* <div css={styles.allConnectorsIcon}> */}
								<img
									src={suggestedAppsCoverIcon}
									alt="suggested apps cover"
									css={styles.allConnectorsIcon}
								/>
								{/* </div> */}
								<div css={styles.suggestedAppDetails}>
									<div>
										{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text, @atlassian/i18n/no-literal-string-in-jsx */}
										<p css={styles.suggestedAppName}>+ 100’s more</p>
										{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text, @atlassian/i18n/no-literal-string-in-jsx */}
										<p css={styles.teammatesLabel}>&nbsp;</p>
									</div>

									<button css={styles.connectButton} type="button" onClick={handleConnectMoreApps}>
										<FormattedMessage {...messages.allConnectorsButton} />
										<ArrowRightIcon label="arrow right" />
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</SpacingWrapper>
	);
};
